// Image features shading (the editor's): each point marked with what its own frame's photo has where the point came
// from: a corner, a line of the photo, a jump or crease of the depth (feats.ts, in photo pixels). A point and its
// camera move together, so the marks hold however its frame is moved. The features are drawn onto a raster as wide
// as the points are apart there (each point stands for a block of depth pixels), so a line marks about one row of
// points and a corner a few. Pure.
import { inverse } from '../align.ts';
import { type PhotoCam, distortN } from './camgeom.ts';
import type { FrameFeats } from './feats.ts';

/** A point's marks: its frame has features (else nothing is known), and what of them it is on. */
export const F_KNOWN = 1, F_CORNER = 2, F_LINE = 4, F_JUMP = 8, F_CREASE = 16;

/** ff drawn on a w × h raster of marks: lines r px either side, corners within rc px. */
export function featRaster(ff: FrameFeats, w: number, h: number, r: number, rc: number): Uint8Array {
  const out = new Uint8Array(w * h);
  const segs = (s: Float32Array, n: number, bit: number, r: number) => {
    for (let k = 0; k < n; k++) {
      const x0 = s[4 * k], y0 = s[4 * k + 1], dx = s[4 * k + 2] - x0, dy = s[4 * k + 3] - y0, L2 = dx * dx + dy * dy;
      const xa = Math.max(0, Math.floor(Math.min(x0, x0 + dx) - r)), xb = Math.min(w - 1, Math.ceil(Math.max(x0, x0 + dx) + r));
      const ya = Math.max(0, Math.floor(Math.min(y0, y0 + dy) - r)), yb = Math.min(h - 1, Math.ceil(Math.max(y0, y0 + dy) + r));
      for (let y = ya; y <= yb; y++) for (let x = xa; x <= xb; x++) {
        const t = L2 ? Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / L2)) : 0, ex = x - x0 - t * dx, ey = y - y0 - t * dy;
        if (ex * ex + ey * ey <= r * r) out[y * w + x] |= bit;
      }
    }
  };
  segs(ff.depth.jump.seg, ff.depth.jump.n, F_JUMP, r);
  segs(ff.depth.crease.seg, ff.depth.crease.n, F_CREASE, r);
  if (ff.segs) segs(ff.segs.seg, ff.segs.n, F_LINE, r);
  if (ff.corners) segs(Float32Array.from({ length: 4 * ff.corners.n }, (_, i) => ff.corners!.xy[2 * (i >> 2) + (i & 1)]), ff.corners.n, F_CORNER, rc);   // a corner: a segment of no length
  return out;
}

/**
 * Per point (P: world, frame: each one's frame), its marks from its frame's features (feats: per frame, its camera
 * placed as the points are and what was found; frames not in it: 0, unknown).
 */
export function featCodes(P: Float32Array, frame: Uint32Array, feats: Map<number, { cam: PhotoCam; ff: FrameFeats }>): Uint8Array {
  const n = frame.length, out = new Uint8Array(n), of = new Map<number, number[]>();
  for (let i = 0; i < n; i++) if (feats.has(frame[i])) (of.get(frame[i]) ?? of.set(frame[i], []).get(frame[i])!).push(i);
  for (const [f, idx] of of) {
    const { cam, ff } = feats.get(f)!;
    // points apart in the photo: the depth pixels each stands for, in photo pixels
    const gap = Math.sqrt((cam.dw * cam.dh) / idx.length) * (cam.w / cam.dw), r = Math.max(1.5, gap / 2);
    const ras = featRaster(ff, cam.w, cam.h, r, gap), Pi = inverse(cam.P), R = Pi.R, t = Pi.t, K = cam.K;
    for (const i of idx) {
      const x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
      const qx = R[0] * x + R[1] * y + R[2] * z + t[0], qy = R[3] * x + R[4] * y + R[5] * z + t[1], qz = R[6] * x + R[7] * y + R[8] * z + t[2];
      out[i] = F_KNOWN;
      if (qz <= 1e-6) continue;
      const [nx, ny] = distortN(qx / qz, qy / qz, cam.dist), u = Math.round(K[0] * nx + K[2]), v = Math.round(K[1] * ny + K[3]);
      if (u >= 0 && v >= 0 && u < cam.w && v < cam.h) out[i] |= ras[v * cam.w + u];
    }
  }
  return out;
}

/** ff without what the shading and the photo overlay don't need (its depth, edge points and corner patches). */
export function slim(ff: FrameFeats): FrameFeats {
  return {
    ...ff, z: new Uint16Array(0), bends: { off: Uint32Array.of(0), pts: new Float32Array(0) },
    corners: ff.corners && { ...ff.corners, desc: new Uint8Array(0) },
  };
}
